import {
  GetMentorStatisticsByFeedbackDTO,
  GetMentorStudentMeetingsDTO,
  NeedsFeedbackDTO,
  SearchAllMeetingsDTO,
} from '@shared/clientTypes/DTOs/MeetingsDTO';
import { ApiDescription, ApiData, RequestsMethod } from '../clientTypes';
import { MentorFeedback, Meeting, MeetingWithHomework, StudentFeedback } from '../common';
import { EducationStrategyReason, MeetingStatus, SystemMeetingType } from '../enums';

export const needsFeedback: ApiDescription = {
  endpoint: '/after-meeting/needs-feedback',
  method: RequestsMethod.Get,
};

export type NeedsFeedback = ApiData<unknown, NeedsFeedbackDTO>;

export const searchMyMeetings: ApiDescription = {
  endpoint: '/meetings/my',
  method: RequestsMethod.Get,
};
export type SearchMyMeetings = ApiData<
  {
    status: 'all' | 'upcoming' | 'feedback' | 'started';
    limit?: number;
    page?: number;
  },
  { data: MeetingWithHomework[]; limit: number; page: number; total: number }
>;

export const getMeeting: ApiDescription = {
  endpoint: '/meeting/:id',
  method: RequestsMethod.Get,
};
export type GetMeeting = ApiData<unknown, Meeting>;

export const getMeetingRecordings: ApiDescription = {
  endpoint: '/meeting/:id/recordings',
  method: RequestsMethod.Get,
};

export type MeetingRecordingLink = {
  recordingType: string;
  fileName: string;
  url: string;
};

export type GetMeetingRecordings = ApiData<
  unknown,
  { recordings: MeetingRecordingLink[]; expiresInSeconds: number }
>;

export const rescheduleMeeting: ApiDescription = {
  endpoint: '/meeting/:id/reschedule',
  method: RequestsMethod.Put,
};

export type RescheduleMeeting = ApiData<Partial<Meeting>, Meeting & { overlapWarning?: string }>;

export const registerMentorFeedback: ApiDescription = {
  endpoint: '/meeting/:id/mentor-feedback',
  method: RequestsMethod.Post,
};
export type CalculateMeetingBillableTime = ApiData<unknown, { billableTime: number }>;

export const calculateMeetingBillableTime: ApiDescription = {
  endpoint: '/meeting/:id/calculate-billable-time',
  method: RequestsMethod.Get,
};

export type RegisterMentorFeedback = ApiData<MentorFeedback, unknown>;

export const registerStudentFeedback: ApiDescription = {
  endpoint: '/meeting/:id/student-feedback',
  method: RequestsMethod.Post,
};
export type RegisterStudentFeedback = ApiData<StudentFeedback, unknown>;

export const markAttended: ApiDescription = {
  endpoint: '/meeting/:id/attended',
  method: RequestsMethod.Post,
};

export type MarkAttended = ApiData<
  {
    userId: string;
    isMentor: boolean;
  },
  unknown
>;

export const createMeeting: ApiDescription = {
  endpoint: '/meeting-new',
  method: RequestsMethod.Post,
};

export type CreateMeeting = ApiData<
  {
    name: string;
    enrollmentId?: string;
    mentorId: string;
    students: any;
    scheduledDate: string;
    agenda?: string;
    meetingType?: SystemMeetingType;
    educationStrategyReason?: EducationStrategyReason;
    educationStrategyNotes?: string;
  },
  Meeting & { overlapWarning?: string }
>;

export const updateMeeting: ApiDescription = {
  endpoint: '/meeting-new/:id',
  method: RequestsMethod.Patch,
};

export type UpdateMeeting = ApiData<
  Partial<Meeting> & {
    mentorId?: string;
  },
  { message: string }
>;

export const getAIGeneratedFeedback = {
  endpoint: '/meeting-new/ai-generated-feedback/:id',
  method: RequestsMethod.Get,
};

export type GetAIGeneratedFeedback = ApiData<
  unknown,
  {
    generationFailed: boolean;
    sessionSummary: string;
    homework: { explanation: string; rate: number };
    engagement: { explanation: string; rate: number };
    progress: { explanation: string; rate: number };
    parentHelp: string;
  }
>;

export const getMentorStatisticsByFeedback: ApiDescription = {
  endpoint: '/meeting-new/statistics/:id',
  method: RequestsMethod.Get,
};

export type GetMentorStatisticsByFeedback = ApiData<unknown, GetMentorStatisticsByFeedbackDTO>;

export const noOfCompletedSessionsCurrentMonth: ApiDescription = {
  endpoint: '/meeting-new/mentor/:id/no-of-completed-sessions-current-month',
  method: RequestsMethod.Get,
};

export type NoOfCompletedSessionsCurrentMonth = ApiData<
  unknown,
  {
    noOfSessions: number;
    noOfMentoringSessions: number;
    noOfOnboardingMeetings: number;
    noOfEducationStrategyMeetings: number;
  }
>;

export const searchAllMeetings: ApiDescription = {
  endpoint: '/meeting-new/all',
  method: RequestsMethod.Get,
};

export type SearchAllMeetings = ApiData<
  {
    limit?: number;
    page?: number;
    studentName?: string;
    mentorName?: string;
    status?: number | null;
    rangeStart?: string;
    rangeEnd?: string;
    course?: string;
    sortBy?: string;
    sortOrder: 'asc' | 'desc' | null;
  },
  SearchAllMeetingsDTO
>;

export const getMentorStudentMeetings = {
  endpoint: '/meeting-new/mentor-student-meetings',
  method: RequestsMethod.Get,
};

export type GetMentorStudentMeetings = ApiData<
  {
    studentId: string;
    enrollmentId?: string;
  },
  GetMentorStudentMeetingsDTO
>;

export const getAllFeedback = {
  endpoint: '/meeting/:id/get-all-feedback',
  method: RequestsMethod.Get,
};

export type GetAllFeedback = ApiData<unknown, MentorFeedback[]>;

export const deprecatedEditMeeting = {
  endpoint: '/meeting/:id/edit',
  method: RequestsMethod.Put,
};

export type DeprecatedEditMeeting = ApiData<Partial<Meeting>, Meeting>;

export const getOwnData = {
  endpoint: '/meeting/list-own',
  method: RequestsMethod.Get,
};

export type GetOwnData = ApiData<unknown, { meetings: Meeting[] }>;

export const appendStudentNotes = {
  endpoint: '/meeting/:id/append-student-notes',
  method: RequestsMethod.Post,
};

export type AppendStudentNotes = ApiData<{ notes: string }, Meeting>;

export const getMeetingNotes = {
  endpoint: '/meeting/:id/get-notes',
  method: RequestsMethod.Get,
};

export type GetMeetingNotes = ApiData<unknown, string>;

export const updateMeetingNotes = {
  endpoint: '/meeting/:id/update-notes',
  method: RequestsMethod.Post,
};

export type UpdateMeetingNotes = ApiData<
  {
    notes: string;
  },
  Meeting
>;

export const getMentorCalendarMeetings = {
  endpoint: '/meeting-new/my-calendar-meetings',
  method: RequestsMethod.Get,
};

export type GetMentorCalendarMeetings = ApiData<
  {
    date?: string;
  },
  MeetingWithHomework[]
>;

export const saveBillableTimeReason = {
  endpoint: '/meeting-new/save-billable-time-reason/:id',
  method: RequestsMethod.Post,
};

export type SaveBillableTimeReason = ApiData<
  {
    billableTimeReason: string;
  },
  Meeting
>;

export const updateMeetingStatus = {
  endpoint: '/meeting-new/:id/status',
  method: RequestsMethod.Patch,
};

export type UpdateMeetingStatus = ApiData<
  {
    status: MeetingStatus;
    force?: boolean;
  },
  {
    status: MeetingStatus;
    sessionStatus: number;
    sessionStatusChanged: boolean;
  }
>;
